import { useCallback } from 'react';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

/**
 * useAssignmentMutations — single source of truth for create/delete/copy.
 *
 * Centralises:
 *  - eligibility / leave / cell-occupied checks (consistent across drag-drop,
 *    quick-assign single, quick-assign all-week)
 *  - borrow detection (firm filter is on but employee belongs elsewhere)
 *  - 409 conflict / generic error toasts
 *  - bulk "all week" path with summary toast and a single board refresh
 */
export function useAssignmentMutations({
  year,
  week,
  filterFirmId,
  leaveByEmpDate,
  assignByCell,
  employeeBookedDates,
  fetchBoard,
  lockedDates,
}) {
  // Helper — bir tarih kilitli mi (resmi tatil / kapatılmış gün). Bu kontrol
  // hem tek hücre atamasını (createAssignmentFor) hem de toplu kopyalama /
  // yayma işlemlerini koruyor: operatör >> tuşuyla bir günün planını sonraki
  // güne aktardığında kilitli bir hedef gün varsa o gün atlanır.
  // Kilitli günler artık yumuşak uyarı — atama yapılmasına izin verilir.
  // Operatör Kurban Bayramı gibi resmi tatillerde nöbetçi çalışan atamak
  // istediğinde bu kural değişikliği gerekiyor (Mayıs 2026).
  const isLocked = (dateStr) => Boolean(lockedDates && lockedDates[dateStr]);

  const createAssignmentFor = useCallback(async (emp, route, dateStr, opts = {}) => {
    const { silent = false, skipRefresh = false, force = false } = opts;
    if (!emp || !route || !dateStr) return { ok: false, reason: 'invalid' };
    if (leaveByEmpDate.has(`${emp.id}|${dateStr}`)) {
      if (!silent) toast.error('Bu çalışan o gün izinli');
      return { ok: false, reason: 'leave' };
    }
    if (assignByCell.has(`${route.id}|${dateStr}`)) {
      if (!silent) toast.error('Bu hücre dolu — önce mevcut atamayı kaldırın');
      return { ok: false, reason: 'cell-full' };
    }
    // Kilitli güne atama yapılırsa kullanıcıya uyarı göster ama POST'u
    // engelleme — operatörün bilinçli kararı (örn. nöbetçi çalışan).
    const lockedReason = lockedDates?.[dateStr] || null;
    const isBorrowed =
      !!filterFirmId &&
      emp.firm_id !== filterFirmId &&
      !(emp.eligible_firm_ids || []).includes(filterFirmId);
    // Otomatik plaka — eğer çalışana Vehicles tabında atanmış bir plaka
    // varsa (`current_vehicle_plate`), atama oluştururken aynı plaka
    // otomatik vehicle_code olarak kaydedilir. Operatör sürücüye bu
    // plaka atanmamışsa Vehicles'tan boş — daha sonra manuel sürükle/bırak
    // ile farklı plaka atanabilir.
    const autoPlate = emp.current_vehicle_plate || undefined;
    try {
      await api.post('/planning/assignments', {
        year,
        week_number: week,
        assignment_date: dateStr,
        company_id: route.company_id,
        depot_id: route.depot_id,
        route_id: route.id,
        employee_id: emp.id,
        firm_id: isBorrowed ? undefined : (filterFirmId || undefined),
        vehicle_code: autoPlate,
        status: 'assigned',
        force,
      });
      if (!silent) {
        const baseMsg = isBorrowed ? 'Ödünç atama oluşturuldu' : 'Atama oluşturuldu';
        const lockedSuffix = lockedReason ? ` · ⚠ kilitli gün (${lockedReason})` : '';
        const plateSuffix = autoPlate ? ` · ${autoPlate}` : '';
        toast.success(`${baseMsg}${plateSuffix}${lockedSuffix}`);
      }
      if (!skipRefresh) fetchBoard();
      return { ok: true, borrowed: isBorrowed };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      // Detect "already booked that day" — distinct from leave-conflict so the
      // UI can offer an explicit override.
      const isDoubleBooked =
        status === 409 &&
        detail && typeof detail === 'object' &&
        detail.code !== 'LEAVE_CONFLICT' &&
        !!detail.existing;
      if (!silent) {
        if (isDoubleBooked) {
          // Caller (drag-drop layer) is expected to surface a confirm dialog
          // and retry with force=true. We still return a structured result.
        } else if (status === 409) {
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Çakışma');
          toast.error(msg);
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Atama eklenemedi');
        }
      }
      return {
        ok: false,
        reason: isDoubleBooked ? 'double-booked' : (status === 409 ? 'conflict' : 'error'),
        existing: isDoubleBooked ? detail.existing : null,
      };
    }
  }, [year, week, filterFirmId, leaveByEmpDate, assignByCell, fetchBoard, lockedDates]);

  const createAssignmentForWeek = useCallback(async (emp, route, weekDates) => {
    let created = 0;
    const skipped = [];
    for (const d of weekDates) {
      if (leaveByEmpDate.has(`${emp.id}|${d}`)) { skipped.push({ d, why: 'izin' }); continue; }
      if (assignByCell.has(`${route.id}|${d}`)) { skipped.push({ d, why: 'rota-dolu' }); continue; }
      const alreadyBooked = (employeeBookedDates.get(emp.id) || new Set()).has(d);
      if (alreadyBooked) { skipped.push({ d, why: 'çift-rezerv' }); continue; }
      const res = await createAssignmentFor(emp, route, d, { silent: true, skipRefresh: true });
      if (res.ok) created += 1;
      else skipped.push({ d, why: res.reason || 'hata' });
    }
    fetchBoard();
    if (created > 0) {
      toast.success(`${created} gün atandı${skipped.length ? `, ${skipped.length} gün atlandı` : ''}`);
    } else {
      toast.error(`Atama yapılamadı (${skipped.length} gün atlandı)`);
    }
    return { created, skipped: skipped.length };
  }, [createAssignmentFor, leaveByEmpDate, assignByCell, employeeBookedDates, fetchBoard]);

  const removeAssignment = useCallback(async (assignment) => {
    try {
      await api.delete(`/planning/assignments/${assignment.id}`);
      toast.success('Atama kaldırıldı');
      fetchBoard();
    } catch (err) {
      toast.error('Kaldırılamadı');
    }
  }, [fetchBoard]);

  // Move an existing assignment to a new (route, date) cell. Implemented as
  // delete-old → create-new so we don't need a new endpoint. The new cell's
  // pre-checks (dolu hücre, leave) still apply. If the employee is already
  // booked at the destination day on another route, the caller can pass
  // `force: true` to allow the double-booking; otherwise we surface the same
  // double-booked result as createAssignmentFor.
  const moveAssignment = useCallback(async (assignment, targetRoute, targetDate, opts = {}) => {
    if (!assignment || !targetRoute || !targetDate) return { ok: false };
    // Same cell? no-op.
    if (assignment.route_id === targetRoute.id && assignment.assignment_date === targetDate) {
      return { ok: true, noop: true };
    }
    if (assignByCell.has(`${targetRoute.id}|${targetDate}`)) {
      toast.error('Hedef hücre dolu — önce mevcut atamayı kaldırın');
      return { ok: false, reason: 'cell-full' };
    }
    if (leaveByEmpDate.has(`${assignment.employee_id}|${targetDate}`)) {
      toast.error('Bu çalışan o gün izinli');
      return { ok: false, reason: 'leave' };
    }
    try {
      // 1) Delete the source assignment
      await api.delete(`/planning/assignments/${assignment.id}`);
      // 2) Create at target — propagate firm_id from the source so attribution
      //    is preserved (don't fall back to filterFirmId here).
      await api.post('/planning/assignments', {
        year,
        week_number: week,
        assignment_date: targetDate,
        company_id: targetRoute.company_id,
        depot_id: targetRoute.depot_id,
        route_id: targetRoute.id,
        employee_id: assignment.employee_id,
        firm_id: assignment.firm_id || undefined,
        shift_name: assignment.shift_name,
        start_time: assignment.start_time,
        vehicle_code: assignment.vehicle_code,
        status: 'assigned',
        force: opts.force === true,
      });
      toast.success('Atama taşındı');
      fetchBoard();
      return { ok: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const isDoubleBooked =
        status === 409 &&
        detail && typeof detail === 'object' &&
        detail.code !== 'LEAVE_CONFLICT' &&
        !!detail.existing;
      if (!isDoubleBooked) {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Taşınamadı'));
      }
      // Best-effort recovery: if delete went through but create failed and we
      // can't auto-retry, refresh so the operator sees the actual state.
      fetchBoard();
      return {
        ok: false,
        reason: isDoubleBooked ? 'double-booked' : (status === 409 ? 'conflict' : 'error'),
        existing: isDoubleBooked ? detail.existing : null,
      };
    }
  }, [year, week, assignByCell, leaveByEmpDate, fetchBoard]);

  const copyFromPreviousWeek = useCallback(async (overwrite = false, filters = null, includeDouble = false) => {
    let srcWeek = week - 1; let srcYear = year;
    if (srcWeek < 1) { srcYear -= 1; srcWeek = 52; }
    // Şub 2026 — Aktif UI filtrelerini backend'e ilet: firma/müşteri/depo/rota
    // veya çalışan filtresi varsa yalnızca o kapsamdaki atamalar kopyalanır.
    // `filters` şekli: { firm_ids?, company_ids?, depot_ids?, route_ids?, employee_ids? }
    const filterPayload = filters || {};
    try {
      const res = await api.post('/planning/copy-week', {
        source_year: srcYear, source_week: srcWeek,
        target_year: year, target_week: week, overwrite, include_double: includeDouble,
        ...filterPayload,
      });
      if (res.data?.success) {
        const d = res.data.data;
        const fPrefix = d.filtered ? '(filtre kapsamında) ' : '';
        toast.success(`${fPrefix}Kopyalandı: ${d.copied} atama, ${d.skipped} atlandı`);
        if (d.leave_skipped > 0) {
          const names = (d.conflicts || []).filter((c) => c.reason === 'leave').map((c) => `${c.employee_name} (${c.date})`);
          toast.warning(`${d.leave_skipped} izinli çalışan kopyalanmadı`, { description: names.slice(0, 8).join(', '), duration: 12000 });
        }
        if (d.forced_conflicts > 0) {
          toast.warning(`${d.forced_conflicts} çift atama çakışmasıyla birlikte kopyalandı`, { duration: 10000 });
        }
        fetchBoard();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kopyalama başarısız');
    }
  }, [year, week, fetchBoard]);

  // Copy every assignment from a source date to the next (or any target) date
  // within the same week. Skips conflicts (employee already booked / on leave
  // on the target date). Single refresh + summary toast at the end.
  const copyDayToDay = useCallback(async (sourceDate, targetDate, sourceAssignments) => {
    if (!sourceAssignments || sourceAssignments.length === 0) {
      toast.info('Kaynak günde atama yok');
      return { copied: 0, skipped: 0 };
    }
    // Hedef gün kilitliyse engellemek yerine sadece bilgi ver — operatör
    // bilinçli olarak kilitli güne kopyalayabilir (örn. Kurban Bayramı
    // nöbetçi rotası). UI hücreleri zaten rose desenle işaretliyor.
    if (isLocked(targetDate)) {
      toast.warning(`Hedef gün kilitli (${lockedDates[targetDate]}) — yine de kopyalanıyor`);
    }
    let copied = 0;
    const skipped = [];
    // Local "what we just created" sets so an in-flight batch can't itself
    // double-book — relying solely on the React-memoised maps would miss the
    // assignment we just POSTed because the board hasn't refetched yet.
    const empBookedLocal = new Set();
    const cellFilledLocal = new Set();
    // Çalışan başına kaynak gün rota sayısı — >1 ise operatör kasıtlı
    // çift atama yapmış demektir; hedef güne yaymada bu deseni koruyoruz.
    const empSourceCount = new Map();
    for (const a of sourceAssignments) {
      if (!a.employee_id) continue;
      empSourceCount.set(a.employee_id, (empSourceCount.get(a.employee_id) || 0) + 1);
    }
    for (const a of sourceAssignments) {
      if (a.status === 'cancelled' || !a.employee_id) { skipped.push(a); continue; }
      if (leaveByEmpDate.has(`${a.employee_id}|${targetDate}`)) { skipped.push(a); continue; }
      const cellKey = `${a.route_id}|${targetDate}`;
      if (assignByCell.has(cellKey) || cellFilledLocal.has(cellKey)) { skipped.push(a); continue; }
      const isIntentionalDouble = (empSourceCount.get(a.employee_id) || 0) > 1;
      const empKey = `${a.employee_id}|${targetDate}`;
      const alreadyBooked = (employeeBookedDates.get(a.employee_id) || new Set()).has(targetDate)
        || empBookedLocal.has(empKey);
      if (alreadyBooked && !isIntentionalDouble) { skipped.push(a); continue; }
      try {
        await api.post('/planning/assignments', {
          year,
          week_number: week,
          assignment_date: targetDate,
          company_id: a.company_id,
          depot_id: a.depot_id,
          route_id: a.route_id,
          employee_id: a.employee_id,
          firm_id: a.firm_id || undefined,
          shift_name: a.shift_name,
          start_time: a.start_time,
          vehicle_code: a.vehicle_code,
          status: 'assigned',
          force: isIntentionalDouble || undefined,
        });
        copied += 1;
        empBookedLocal.add(empKey);
        cellFilledLocal.add(cellKey);
      } catch {
        skipped.push(a);
      }
    }
    fetchBoard();
    if (copied > 0) {
      toast.success(`${copied} atama kopyalandı${skipped.length ? `, ${skipped.length} atlandı` : ''}`);
    } else {
      toast.error(`Kopyalanamadı (${skipped.length} atlandı)`);
    }
    return { copied, skipped: skipped.length };
  }, [year, week, leaveByEmpDate, assignByCell, employeeBookedDates, fetchBoard, lockedDates]);

  // "Yay tüm güne" — spread one day's plan across every later WEEKDAY in the
  // same week. Çağıran tarafın açıkça verdiği `targetDates` (operatörün UI'da
  // gerçekten görünür kıldığı günler) kullanılır; verilmezse fallback olarak
  // Pzt..Cmt slice'ı (Pazar hariç) hesaplanır. Kilitli günler her durumda
  // atlanır — operatör 14 Mayıs gibi bir kilitli güne yanlışlıkla atama
  // yapamaz.
  //
  // İKİLİ ATAMA KORUNUMU (May 03, 2026 fix):
  //   Kaynak günde aynı çalışan birden fazla rotada görevliyse (operatör
  //   "Çift atama onayı"nı bilinçli kabul etmiş demektir) bu desen hedef
  //   günlere de aynı şekilde yayılır — yerel `alreadyBooked` kontrolünü
  //   bu çalışan için atlıyor, backend'e `force:true` gönderiyoruz. Aksi
  //   halde Pzt'de LM-01 + LM-02 üzerinde olan Ahmet, Sal'a sadece bir
  //   rotada (ilk işlenen) yayılıyor, ikinci rota "çift-rezerv" diyerek
  //   sessizce düşüyordu.
  const spreadDayToRestOfWeek = useCallback(async (sourceDate, allDates, sourceAssignments, targetDates = null) => {
    if (!sourceAssignments || sourceAssignments.length === 0) {
      toast.info('Kaynak günde atama yok');
      return { copied: 0, skipped: 0 };
    }
    let targets;
    if (Array.isArray(targetDates)) {
      targets = targetDates;
    } else {
      const idx = allDates.indexOf(sourceDate);
      if (idx < 0 || idx >= allDates.length - 1) return { copied: 0, skipped: 0 };
      // slice end is exclusive → 6 stops just before Sunday (index 6).
      targets = allDates.slice(idx + 1, 6);
    }
    if (targets.length === 0) {
      toast.info('Yayılacak gün yok');
      return { copied: 0, skipped: 0 };
    }
    // Çalışan başına kaynak gün rota sayısı — >1 ise kasıtlı çift-atama.
    const empSourceCount = new Map();
    for (const a of sourceAssignments) {
      if (!a.employee_id) continue;
      empSourceCount.set(a.employee_id, (empSourceCount.get(a.employee_id) || 0) + 1);
    }
    let copied = 0;
    const skipped = [];
    let lockedHits = 0; // sayım — kilit gerçek hata değil, kullanıcı uyarısı için
    let intentionalDoubles = 0; // raporlama — operatör görsün ki çift atamalar yayıldı
    const empBookedLocal = new Set();
    const cellFilledLocal = new Set();
    for (const tDate of targets) {
      // Kilitli günlere artık yayılır — sadece sayıyoruz ki son toast'ta
      // "X tatil günlerine de yayıldı" diyebilelim.
      if (isLocked(tDate)) lockedHits += 1;
      for (const a of sourceAssignments) {
        if (a.status === 'cancelled' || !a.employee_id) { skipped.push({ a, tDate, why: 'iptal' }); continue; }
        if (leaveByEmpDate.has(`${a.employee_id}|${tDate}`)) { skipped.push({ a, tDate, why: 'izin' }); continue; }
        const cellKey = `${a.route_id}|${tDate}`;
        if (assignByCell.has(cellKey) || cellFilledLocal.has(cellKey)) { skipped.push({ a, tDate, why: 'dolu' }); continue; }
        const isIntentionalDouble = (empSourceCount.get(a.employee_id) || 0) > 1;
        const empKey = `${a.employee_id}|${tDate}`;
        const alreadyBooked = (employeeBookedDates.get(a.employee_id) || new Set()).has(tDate)
          || empBookedLocal.has(empKey);
        // Sadece KASITLI olmayan ikinci atamalarda çakışma kabul edilir;
        // operatörün kaynakta zaten onayladığı çift-atamayı yine bloklamak
        // anlamsız olur (kullanıcı bug raporu).
        if (alreadyBooked && !isIntentionalDouble) {
          skipped.push({ a, tDate, why: 'çift-rezerv' });
          continue;
        }
        try {
          await api.post('/planning/assignments', {
            year, week_number: week, assignment_date: tDate,
            company_id: a.company_id, depot_id: a.depot_id, route_id: a.route_id,
            employee_id: a.employee_id, firm_id: a.firm_id || undefined,
            shift_name: a.shift_name, start_time: a.start_time, vehicle_code: a.vehicle_code,
            status: 'assigned',
            // Kaynakta bilinçli çift-atama varsa backend 409'unu da bypass et.
            force: isIntentionalDouble || undefined,
          });
          copied += 1;
          if (isIntentionalDouble && alreadyBooked) intentionalDoubles += 1;
          empBookedLocal.add(empKey);
          cellFilledLocal.add(cellKey);
        } catch {
          skipped.push({ a, tDate, why: 'hata' });
        }
      }
    }
    fetchBoard();
    if (copied > 0) {
      const lockedNote = lockedHits > 0 ? ` (${lockedHits} kilitli güne dahil)` : '';
      const doubleNote = intentionalDoubles > 0 ? ` · ${intentionalDoubles} çift-atama korundu` : '';
      toast.success(`${copied} atama yayıldı${lockedNote}${doubleNote}${skipped.length ? `, ${skipped.length} atlandı` : ''}`);
    } else {
      toast.error(`Yayılamadı (${skipped.length} atlandı)`);
    }
    return { copied, skipped: skipped.length };
  }, [year, week, leaveByEmpDate, assignByCell, employeeBookedDates, fetchBoard, lockedDates]);

  return {
    createAssignmentFor,
    createAssignmentForWeek,
    removeAssignment,
    moveAssignment,
    copyFromPreviousWeek,
    copyDayToDay,
    spreadDayToRestOfWeek,
  };
}
